import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, makeStyles } from '@material-ui/core';

import { useDebounce } from '../../../shared/hooks/use-debounce.hook';
import { DEFAULT_ROWS_PER_PAGE } from '../../../shared/constants/table.constants';
import { MIN_SEARCH_LENGTH } from '../../../shared/constants/search.constants';
import { EntityStatusFilter, toEntityListFilters } from '../../../shared/constants/entity-status-filter.constants';
import { useAdminGetDeliveryCities } from '../../hooks/admin-get-delivery-cities.hook';
import { useCreateDeliveryCity } from '../../hooks/create-delivery-city.hook';
import { ADMIN_DELIVERY_CITIES_APP_PATHS } from '../../constants/delivery-cities-app-paths.constants';
import { ALL_DELIVERY_CITY_ZONES } from '../../constants/delivery-city-filters.constants';
import { DeliveryCitySortColumn, DeliveryCitySortDirection } from '../../types/delivery-city.type';
import { CreateDeliveryCityRequestDTO } from '../../types/dtos/create-delivery-city.dto';
import { DeliveryCitiesListFilters } from '../../components/Containers/DeliveryCitiesListFilters.component';
import { DeliveryCitiesTable } from '../../components/Tables/DeliveryCitiesTable.component';
import { CreateDeliveryCityDialog } from '../../components/Modals/CreateDeliveryCityDialog.component';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '86svh',
    gap: '0.7rem',

    '& > h2': {
      margin: 0,
    },
  },

  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },

  createButton: {
    marginLeft: 'auto',
    whiteSpace: 'nowrap',
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

export default function AdminDeliveryCitiesListView() {
  const classes = useStyles();
  const history = useHistory();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [search, setSearch] = useState('');
  const [zoneFilter, setZoneFilter] = useState<string>(ALL_DELIVERY_CITY_ZONES);
  const [statusFilter, setStatusFilter] = useState<EntityStatusFilter>('all');
  const [sortBy, setSortBy] = useState<DeliveryCitySortColumn>('name');
  const [sortDirection, setSortDirection] = useState<DeliveryCitySortDirection>('asc');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const debouncedSearch = useDebounce(search, 400).trim();

  const { isLoading, isRefetching, totalCount, deliveryCities } = useAdminGetDeliveryCities({
    page: page + 1,
    limit: rowsPerPage,
    sortBy,
    sortDirection,
    filters: {
      ...(debouncedSearch.length >= MIN_SEARCH_LENGTH ? { search: debouncedSearch } : {}),
      ...(zoneFilter !== ALL_DELIVERY_CITY_ZONES ? { zoneId: zoneFilter } : {}),
      ...toEntityListFilters(statusFilter),
    },
  });
  const { createDeliveryCity, isSubmitting: isCreating } = useCreateDeliveryCity();

  const withPageReset =
    <Value,>(setValue: (value: Value) => void) =>
    (value: Value) => {
      setValue(value);
      setPage(0);
    };

  const handleSortChange = (column: DeliveryCitySortColumn) => {
    setSortDirection(column === sortBy && sortDirection === 'asc' ? 'desc' : 'asc');
    setSortBy(column);
    setPage(0);
  };

  const handleCreateDeliveryCity = async (data: CreateDeliveryCityRequestDTO) => {
    const createdDeliveryCity = await createDeliveryCity(data);
    if (!createdDeliveryCity) return;

    setIsCreateDialogOpen(false);
    history.push(ADMIN_DELIVERY_CITIES_APP_PATHS.DELIVERY_CITY_DETAILS_PATH(createdDeliveryCity.id));
  };

  return (
    <section className={classes.root}>
      <h2>Villes de livraison</h2>
      <div className={classes.toolbar}>
        <DeliveryCitiesListFilters
          search={search}
          zoneFilter={zoneFilter}
          statusFilter={statusFilter}
          onSearchChange={withPageReset(setSearch)}
          onZoneFilterChange={withPageReset(setZoneFilter)}
          onStatusFilterChange={withPageReset(setStatusFilter)}
        />
        <Button
          size="small"
          variant="contained"
          className={classes.createButton}
          onClick={() => setIsCreateDialogOpen(true)}
        >
          Créer une ville de livraison
        </Button>
      </div>
      <DeliveryCitiesTable
        deliveryCities={deliveryCities}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        isLoading={isLoading || isRefetching}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onPageChange={setPage}
        onRowsPerPageChange={withPageReset(setRowsPerPage)}
        onSortChange={handleSortChange}
      />
      <CreateDeliveryCityDialog
        open={isCreateDialogOpen}
        isSubmitting={isCreating}
        onSubmit={handleCreateDeliveryCity}
        onClose={() => setIsCreateDialogOpen(false)}
      />
    </section>
  );
}
